import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import {
  LegendItem,
  MONO_FONT,
  PAGE_FONT,
  SectionHead,
  headCellSx,
  numCellSx,
  textCellSx,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import {
  formatCompactCurrency,
  formatCount,
  formatCurrency,
} from "../../components/metrics/metricsFormat";
import { fieldSx } from "../manage-catalog/constants";
import { isMobile } from "../../globalStyles/mobile";
import { returnsAnalysisService } from "../../utils/services/returnsAnalysisService";
import { orderService } from "../../utils/services/orderService";
import {
  clearSelection,
  retainAvailableSelection,
} from "../../utils/channelSelection";
import { returnService } from "../../utils/services/returnService";
import { downloadReturnsAnalysisHtml } from "../../utils/returns-analysis-html-export";
import ReturnViewModal from "../../components/return-view-modal";
import { barWidthPercent, maxPositive } from "./returnsAnalysisUi";
import { openAgeCacheKey, OpenAgeMemoryCache } from "./openReturnAgeCache";
import OpenReturnAgeDrawer from "./OpenReturnAgeDrawer";
import { skuReasonCacheKey, SkuReasonMemoryCache } from "./skuReturnReasonsCache";
import SkuReturnInvestigationDrawer from "./SkuReturnInvestigationDrawer";
import {
  skuReasonReturnsCacheKey,
  SkuReasonReturnsMemoryCache,
} from "./skuReasonReturnsCache";

const FIELD_HEIGHT = 36;
const fieldControlSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: FIELD_HEIGHT,
    fontFamily: PAGE_FONT,
    fontSize: 13,
    borderRadius: "8px",
  },
};

const toolbarButtonSx = {
  height: FIELD_HEIGHT,
  fontSize: 13,
  fontWeight: 600,
  borderRadius: "8px",
  textTransform: "none",
};

const FieldLabel = ({ children, colors }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      color: colors.textTertiary,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      mb: 0.5,
      lineHeight: 1,
    }}
  >
    {children}
  </Typography>
);

/** One label + control column, so every toolbar field shares the same shape. */
const ToolbarField = ({ label, colors, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    <FieldLabel colors={colors}>{label}</FieldLabel>
    {children}
  </Box>
);

const todayKey = () => new Date().toISOString().slice(0, 10);
const addDays = (dateKey, days) => {
  const d = new Date(`${dateKey}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Multi-select Channel filter: Menu-based (not a plain MUI Select) so it can
 * hold a search box + Select All / Clear All without a new dependency —
 * everything here (Menu, MenuItem, Checkbox, ListItemText, TextField) is
 * already used elsewhere in the app.
 */
const ChannelMultiSelect = ({ options, selected, onChange, disabled, ui }) => {
  const { colors } = ui;
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);

  const filteredOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? options.filter((o) => o.toLowerCase().includes(needle)) : options;
  }, [options, search]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const toggle = (value) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  const summaryLabel =
    selected.length === 0
      ? "All Channels"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} channels`;

  return (
    <>
      <Button
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
        sx={{
          ...toolbarButtonSx,
          justifyContent: "space-between",
          minWidth: 170,
          maxWidth: 220,
          px: 1.5,
          backgroundColor: colors.bgPaper,
          borderColor: ui.borderStrong,
          color: selected.length ? colors.textPrimary : colors.textSecondary,
          "&:hover": { borderColor: ui.borderStrong, backgroundColor: colors.bgPaper },
        }}
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {summaryLabel}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 270, maxHeight: 400, mt: 0.5 } }}
      >
        <Box sx={{ px: 1.25, pt: 0.75, pb: 0.5 }}>
          <TextField
            size="small"
            placeholder="Search channels…"
            fullWidth
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ fontSize: 15, color: colors.textTertiary, mr: 0.5 }} />
              ),
            }}
            sx={fieldControlSx}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.25, py: 0.25 }}>
          <Button
            size="small"
            disabled={options.length === 0}
            onClick={() => onChange([...options])}
            sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            Select All
          </Button>
          <Button
            size="small"
            disabled={selected.length === 0}
            onClick={() => onChange([])}
            sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            Clear All
          </Button>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
          {filteredOptions.length === 0 ? (
            <Typography sx={{ px: 2, py: 1.5, fontSize: 12.5, color: colors.textTertiary }}>
              No channels match &quot;{search}&quot;
            </Typography>
          ) : (
            filteredOptions.map((option) => (
              <MenuItem
                key={option}
                onClick={() => toggle(option)}
                dense
                sx={{ fontSize: 13, py: 0.4 }}
              >
                <Checkbox size="small" checked={selected.includes(option)} sx={{ p: 0.5, mr: 0.5 }} />
                <ListItemText primary={option} primaryTypographyProps={{ fontSize: 13 }} />
              </MenuItem>
            ))
          )}
        </Box>
      </Menu>
    </>
  );
};

const KpiCard = ({ label, value, sub, tooltip, ui }) => (
  <Box
    sx={{
      ...ui.card,
      padding: "14px 16px 13px",
      minHeight: 104,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          color: ui.colors.textSecondary,
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: ui.colors.textTertiary }} />
        </Tooltip>
      )}
    </Box>
    <Typography
      sx={{
        fontSize: 24,
        fontWeight: 650,
        letterSpacing: "-0.01em",
        fontVariantNumeric: "tabular-nums",
        color: ui.colors.textPrimary,
        lineHeight: 1.15,
      }}
    >
      {value}
    </Typography>
    <Box sx={{ mt: "auto", pt: 0.9, minHeight: 16 }}>
      {sub != null && (
        <Typography sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}>{sub}</Typography>
      )}
    </Box>
  </Box>
);

const StatusBar = ({ label, color, units, value, maxUnits, ui }) => {
  const width = barWidthPercent(units, maxUnits);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <LegendItem color={color} label={label} />
        <Typography sx={{ fontSize: 12.5, color: ui.colors.textPrimary, fontWeight: 600 }}>
          {formatCount(units)} units · {formatCurrency(value)}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          borderRadius: "4px",
          backgroundColor: ui.surfaceAlt,
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: `${width}%`, height: "100%", backgroundColor: color }} />
      </Box>
    </Box>
  );
};

const ReturnsAnalysisPage = () => {
  const ui = useMetricsUi();
  const { colors } = ui;
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const clientId = selectedClientData?._id || storedClientId;

  const [startDate, setStartDate] = useState(() => addDays(todayKey(), -29));
  const [endDate, setEndDate] = useState(() => todayKey());
  const [channelOptions, setChannelOptions] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [skuSearchInput, setSkuSearchInput] = useState("");
  const [skuSearch, setSkuSearch] = useState("");
  const [skuPage, setSkuPage] = useState(0);
  const [skuRowsPerPage, setSkuRowsPerPage] = useState(10);
  const [skuSort, setSkuSort] = useState({ field: "returned_units", dir: "desc" });

  const ageCacheRef = useRef(new OpenAgeMemoryCache());
  const [ageBucket, setAgeBucket] = useState(null);
  const [ageStage, setAgeStage] = useState(null);
  const [agePage, setAgePage] = useState(0);
  const [ageRowsPerPage, setAgeRowsPerPage] = useState(25);
  const [ageSort, setAgeSort] = useState({ field: "age_days", dir: "desc" });
  const [agePayload, setAgePayload] = useState(null);
  const [ageLoading, setAgeLoading] = useState(false);
  const [ageError, setAgeError] = useState(null);
  const [returnDetail, setReturnDetail] = useState(null);

  const skuReasonCacheRef = useRef(new SkuReasonMemoryCache());
  const [skuReasonSku, setSkuReasonSku] = useState(null);
  const [skuReasonPayload, setSkuReasonPayload] = useState(null);
  const [skuReasonLoading, setSkuReasonLoading] = useState(false);
  const [skuReasonError, setSkuReasonError] = useState(null);

  const skuEvidenceCacheRef = useRef(new SkuReasonReturnsMemoryCache());
  const [skuEvidenceReason, setSkuEvidenceReason] = useState(null);
  const [skuEvidencePayload, setSkuEvidencePayload] = useState(null);
  const [skuEvidenceLoading, setSkuEvidenceLoading] = useState(false);
  const [skuEvidenceError, setSkuEvidenceError] = useState(null);
  const [skuEvidencePage, setSkuEvidencePage] = useState(0);
  const [skuEvidenceRowsPerPage, setSkuEvidenceRowsPerPage] = useState(25);
  const [skuEvidenceSort, setSkuEvidenceSort] = useState({
    field: "requested_at",
    dir: "desc",
  });
  const [skuEvidenceRetryNonce, setSkuEvidenceRetryNonce] = useState(0);

  const invalidRange = !startDate || !endDate || startDate > endDate;

  useEffect(() => {
    const handle = setTimeout(() => setSkuSearch(skuSearchInput.trim().toLowerCase()), 250);
    return () => clearTimeout(handle);
  }, [skuSearchInput]);

  useEffect(() => {
    setSkuPage(0);
  }, [skuSearch, payload]);

  useEffect(() => {
    if (!clientId) {
      setChannelOptions([]);
      setSelectedChannels(clearSelection);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await orderService.fetchOrderSource("", clientId, 50, 0);
        if (cancelled) return;
        const options = (data?.data || [])
          .map((row) => row?._id)
          .filter((value) => value != null && value !== "");
        setChannelOptions(options);
        // Same array when nothing changed, so the report is not fetched twice.
        setSelectedChannels((prev) => retainAvailableSelection(prev, options));
      } catch {
        if (!cancelled) {
          setChannelOptions([]);
          setSelectedChannels(clearSelection);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId || invalidRange) {
      setPayload(null);
      setLoading(false);
      return;
    }
    // Enter loading state immediately on every filter change (including
    // Refresh). `cancelled` is the same stale-response guard already used by
    // Sales Analysis / Lost Sales Analysis: React runs this cleanup before
    // the next effect fires, so a late response from a superseded filter
    // selection can never land in state after a newer one already has.
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await returnsAnalysisService.fetch({
          clientId,
          startDate,
          endDate,
          channels: selectedChannels,
        });
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) {
          setPayload(null);
          setError(
            err?.response?.data?.message || err?.message || "Failed to load returns analysis",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, startDate, endDate, selectedChannels, invalidRange, reloadNonce]);

  useEffect(() => {
    ageCacheRef.current.clear();
    skuReasonCacheRef.current.clear();
    skuEvidenceCacheRef.current.clear();
    setAgeBucket(null);
    setAgeStage(null);
    setAgePage(0);
    setAgePayload(null);
    setAgeError(null);
    setReturnDetail(null);
    setSkuReasonSku(null);
    setSkuReasonPayload(null);
    setSkuReasonError(null);
    setSkuEvidenceReason(null);
    setSkuEvidencePayload(null);
    setSkuEvidenceError(null);
    setSkuEvidencePage(0);
  }, [clientId, startDate, endDate, selectedChannels, reloadNonce]);

  useEffect(() => {
    if (!ageBucket || !clientId || invalidRange) {
      return undefined;
    }
    const request = {
      clientId,
      startDate,
      endDate,
      channels: selectedChannels,
      ageBucket,
      stage: ageStage,
      skip: agePage * ageRowsPerPage,
      limit: ageRowsPerPage,
      sortField: ageSort.field,
      sortDir: ageSort.dir,
    };
    const cacheKey = openAgeCacheKey(request);
    const cached = ageCacheRef.current.get(cacheKey);
    if (cached) {
      setAgePayload(cached);
      setAgeLoading(false);
      setAgeError(null);
      return undefined;
    }
    let cancelled = false;
    setAgeLoading(true);
    setAgeError(null);
    (async () => {
      try {
        const data = await returnsAnalysisService.fetchOpenAge(request);
        if (cancelled) return;
        ageCacheRef.current.set(cacheKey, data);
        setAgePayload(data);
      } catch (err) {
        if (!cancelled) {
          setAgePayload(null);
          setAgeError(
            err?.response?.data?.message || err?.message || "Failed to load open returns",
          );
        }
      } finally {
        if (!cancelled) setAgeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    ageBucket,
    ageStage,
    agePage,
    ageRowsPerPage,
    ageSort,
    clientId,
    startDate,
    endDate,
    selectedChannels,
    invalidRange,
  ]);

  useEffect(() => {
    if (!skuReasonSku || !clientId || invalidRange) {
      return undefined;
    }
    const request = {
      clientId,
      startDate,
      endDate,
      channels: selectedChannels,
      sku: skuReasonSku,
    };
    const cacheKey = skuReasonCacheKey(request);
    const cached = skuReasonCacheRef.current.get(cacheKey);
    if (cached) {
      setSkuReasonPayload(cached);
      setSkuReasonLoading(false);
      setSkuReasonError(null);
      return undefined;
    }
    let cancelled = false;
    setSkuReasonLoading(true);
    setSkuReasonError(null);
    (async () => {
      try {
        const data = await returnsAnalysisService.fetchSkuReasons(request);
        if (cancelled) return;
        skuReasonCacheRef.current.set(cacheKey, data);
        setSkuReasonPayload(data);
      } catch (err) {
        if (!cancelled) {
          setSkuReasonPayload(null);
          setSkuReasonError(
            err?.response?.data?.message || err?.message || "Failed to load SKU return reasons",
          );
        }
      } finally {
        if (!cancelled) setSkuReasonLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skuReasonSku, clientId, startDate, endDate, selectedChannels, invalidRange]);

  useEffect(() => {
    if (!skuReasonSku || !skuEvidenceReason || !clientId || invalidRange) {
      return undefined;
    }
    const request = {
      clientId,
      startDate,
      endDate,
      channels: selectedChannels,
      sku: skuReasonSku,
      reason: skuEvidenceReason,
      skip: skuEvidencePage * skuEvidenceRowsPerPage,
      limit: skuEvidenceRowsPerPage,
      sortField: skuEvidenceSort.field,
      sortDir: skuEvidenceSort.dir,
    };
    const cacheKey = skuReasonReturnsCacheKey(request);
    const cached = skuEvidenceCacheRef.current.get(cacheKey);
    if (cached) {
      setSkuEvidencePayload(cached);
      setSkuEvidenceLoading(false);
      setSkuEvidenceError(null);
      return undefined;
    }
    let cancelled = false;
    setSkuEvidenceLoading(true);
    setSkuEvidenceError(null);
    (async () => {
      try {
        const data = await returnsAnalysisService.fetchSkuReasonReturns(request);
        if (cancelled) return;
        skuEvidenceCacheRef.current.set(cacheKey, data);
        setSkuEvidencePayload(data);
      } catch (err) {
        if (!cancelled) {
          setSkuEvidencePayload(null);
          setSkuEvidenceError(
            err?.response?.data?.message || err?.message || "Failed to load return evidence",
          );
        }
      } finally {
        if (!cancelled) setSkuEvidenceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    skuReasonSku,
    skuEvidenceReason,
    skuEvidencePage,
    skuEvidenceRowsPerPage,
    skuEvidenceSort,
    skuEvidenceRetryNonce,
    clientId,
    startDate,
    endDate,
    selectedChannels,
    invalidRange,
  ]);

  const chartData = useMemo(
    () =>
      (payload?.by_date || []).map((row) => ({
        date: row.date.slice(5),
        fullDate: row.date,
        return_units: row.return_units,
        return_value: row.return_value,
      })),
    [payload],
  );

  const filteredSkuRows = useMemo(() => {
    const rows = payload?.sku || [];
    const filtered = skuSearch
      ? rows.filter((row) => row.sku.toLowerCase().includes(skuSearch))
      : rows;
    const sorted = [...filtered].sort((a, b) => {
      const dir = skuSort.dir === "asc" ? 1 : -1;
      const av = a[skuSort.field] ?? -1;
      const bv = b[skuSort.field] ?? -1;
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
    return sorted;
  }, [payload, skuSearch, skuSort]);

  const pagedSkuRows = filteredSkuRows.slice(
    skuPage * skuRowsPerPage,
    skuPage * skuRowsPerPage + skuRowsPerPage,
  );

  const toggleSort = (field) => {
    setSkuSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "desc" },
    );
  };

  const openAgeBucket = (bucketKey) => {
    if (ageBucket === bucketKey) {
      setAgeBucket(null);
      return;
    }
    setSkuReasonSku(null);
    setAgeStage(null);
    setAgePage(0);
    setAgeSort({ field: "age_days", dir: "desc" });
    setAgePayload(null);
    setAgeError(null);
    setAgeBucket(bucketKey);
  };

  const openSkuReason = (sku) => {
    if (!sku) return;
    if (skuReasonSku === sku) {
      setSkuReasonSku(null);
      setSkuEvidenceReason(null);
      setSkuEvidencePayload(null);
      return;
    }
    setAgeBucket(null);
    const cached = skuReasonCacheRef.current.get(
      skuReasonCacheKey({
        clientId,
        startDate,
        endDate,
        channels: selectedChannels,
        sku,
      }),
    );
    setSkuReasonPayload(cached || null);
    setSkuReasonError(null);
    setSkuReasonLoading(!cached);
    setSkuReasonSku(sku);
    setSkuEvidenceReason(null);
    setSkuEvidencePayload(null);
    setSkuEvidenceError(null);
    setSkuEvidencePage(0);
  };

  const toggleAgeSort = (field) => {
    setAgePage(0);
    setAgeSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "desc" },
    );
  };

  const handleAgeStageChange = (nextStage) => {
    setAgePage(0);
    setAgeStage(nextStage);
  };

  const handleReturnRowClick = async (row) => {
    if (!row?._id) return;
    try {
      const full = await returnService.fetchById(row._id);
      setReturnDetail(full);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to load return";
      if (skuEvidenceReason) setSkuEvidenceError(message);
      else setAgeError(message);
    }
  };

  const openSkuEvidence = (reason) => {
    if (!reason) return;
    setSkuEvidencePage(0);
    setSkuEvidenceSort({ field: "requested_at", dir: "desc" });
    const cached = skuEvidenceCacheRef.current.get(
      skuReasonReturnsCacheKey({
        clientId,
        startDate,
        endDate,
        channels: selectedChannels,
        sku: skuReasonSku,
        reason,
        skip: 0,
        limit: skuEvidenceRowsPerPage,
        sortField: "requested_at",
        sortDir: "desc",
      }),
    );
    setSkuEvidencePayload(cached || null);
    setSkuEvidenceError(null);
    setSkuEvidenceRetryNonce(0);
    setSkuEvidenceReason(reason);
  };

  const backToSkuReasons = () => {
    setSkuEvidenceReason(null);
    setSkuEvidencePayload(null);
    setSkuEvidenceError(null);
    setSkuEvidencePage(0);
  };

  const retrySkuEvidence = () => {
    const request = {
      clientId,
      startDate,
      endDate,
      channels: selectedChannels,
      sku: skuReasonSku,
      reason: skuEvidenceReason,
      skip: skuEvidencePage * skuEvidenceRowsPerPage,
      limit: skuEvidenceRowsPerPage,
      sortField: skuEvidenceSort.field,
      sortDir: skuEvidenceSort.dir,
    };
    skuEvidenceCacheRef.current.delete(skuReasonReturnsCacheKey(request));
    setSkuEvidencePayload(null);
    setSkuEvidenceRetryNonce((n) => n + 1);
  };

  const toggleEvidenceSort = (field) => {
    setSkuEvidencePage(0);
    setSkuEvidenceSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "desc" },
    );
  };

  const maxStatusUnits = maxPositive([
    payload?.status?.in_progress?.units,
    payload?.status?.complete?.units,
  ]);
  const maxChannelUnits = maxPositive((payload?.by_channel || []).map((row) => row.return_units));
  const maxAgeUnits = maxPositive((payload?.age?.buckets || []).map((b) => b.units));

  const tooltipStyle = {
    backgroundColor: colors.bgPaper,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    color: colors.textPrimary,
    fontSize: 12,
  };

  // Show the shared skeleton for every load — including a filter change with
  // data already on screen — so stale KPI/chart/table values are never
  // presented as though they belong to the newly selected filters. Filter
  // controls themselves live outside this block and stay interactive.
  const pageLoading = Boolean(clientId) && loading;
  const channelFilterLabel =
    selectedChannels.length === 0
      ? "All channels — select one or more above to filter every section on this page"
      : selectedChannels.length === 1
        ? `Filtered to ${selectedChannels[0]}`
        : `Filtered to ${selectedChannels.length} channels: ${selectedChannels.join(", ")}`;

  return (
    <Box
      sx={{
        backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
      }}
    >
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: isMobile() ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="RA"
          title="Returns Analysis"
          clientName={selectedClientData?.business_name || selectedClientData?.name}
          right={
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.25 }}>
              {!storedClientId && (
                <ToolbarField label="Client" colors={colors}>
                  <ClientAutocomplete
                    width={200}
                    selectedClient={selectedClientData}
                    onClientChange={(client) => setSelectedClientData(client)}
                    hideLabel
                  />
                </ToolbarField>
              )}
              <ToolbarField label="Start Date" colors={colors}>
                <TextField
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={invalidRange}
                  sx={{ ...fieldControlSx, width: 152 }}
                />
              </ToolbarField>
              <ToolbarField label="End Date" colors={colors}>
                <TextField
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={invalidRange}
                  sx={{ ...fieldControlSx, width: 152 }}
                />
              </ToolbarField>
              {channelOptions.length >= 2 && (
                <ToolbarField label="Channel" colors={colors}>
                  <ChannelMultiSelect
                    options={channelOptions}
                    selected={selectedChannels}
                    onChange={setSelectedChannels}
                    disabled={loading}
                    ui={ui}
                  />
                </ToolbarField>
              )}
              <Button
                variant="outlined"
                onClick={() => setReloadNonce((n) => n + 1)}
                disabled={loading || !clientId}
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ...toolbarButtonSx,
                  borderColor: ui.borderStrong,
                  color: colors.textSecondary,
                  backgroundColor: colors.bgPaper,
                }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  downloadReturnsAnalysisHtml(payload, {
                    clientName:
                      selectedClientData?.business_name || selectedClientData?.name,
                  })
                }
                disabled={loading || !payload || invalidRange}
                sx={{
                  ...toolbarButtonSx,
                  borderColor: ui.borderStrong,
                  color: colors.textSecondary,
                  backgroundColor: colors.bgPaper,
                }}
              >
                Download HTML
              </Button>
            </Box>
          }
        />

        {!clientId && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            Select a client to load returns analysis.
          </Alert>
        )}
        {invalidRange && clientId && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
            Start date must not be after end date.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}
        {!pageLoading && payload && payload.diagnostics?.unmapped?.count > 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            {formatCount(payload.diagnostics.unmapped.count)} return
            {payload.diagnostics.unmapped.count === 1 ? "" : "s"} (
            {formatCount(payload.diagnostics.unmapped.units)} unit
            {payload.diagnostics.unmapped.units === 1 ? "" : "s"}) in this period have
            a status this dashboard doesn't recognize yet and are excluded from every
            metric above
            {payload.diagnostics.unmapped.statuses?.length > 0 && (
              <>
                :{" "}
                {payload.diagnostics.unmapped.statuses
                  .map((s) => `${s.status || "(no status)"} (${formatCount(s.count)})`)
                  .join(", ")}
              </>
            )}
            .
          </Alert>
        )}

        {pageLoading && <SalesPerformancePageLoading title="Loading returns analysis..." kpiCount={4} />}

        {!pageLoading && payload && (
          <>
            {/* ---------- KPI row ---------- */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile()
                  ? "repeat(2, 1fr)"
                  : "repeat(auto-fit, minmax(190px, 1fr))",
                gap: 1.5,
                mb: 2.5,
              }}
            >
              <KpiCard
                ui={ui}
                label="Total Return Units"
                value={formatCount(payload.totals.return_units)}
                sub={`${payload.start_date} – ${payload.end_date}`}
              />
              <KpiCard
                ui={ui}
                label="Return Value ($)"
                value={formatCurrency(payload.totals.return_value)}
                sub="Merchandise subtotal, not a cash-refund ledger"
                tooltip={payload.meta?.return_value_basis}
              />
              <KpiCard
                ui={ui}
                label="Open Return Units"
                value={formatCount(payload.totals.open_return_units)}
                sub={`${formatCount(payload.totals.open_return_count)} open returns`}
              />
              <KpiCard
                ui={ui}
                label="% Open Returns < 30 Days"
                value={
                  payload.totals.pct_open_under_30 == null
                    ? "—"
                    : `${payload.totals.pct_open_under_30}%`
                }
                sub="Of currently open (in-progress) returns"
              />
            </Box>

            {/* ---------- Status section ---------- */}
            <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 2, mb: 2 }}>
              <SectionHead
                title="Status"
                subtitle="In Progress (Draft / Expected / Received / Refunded / Shipped / Authorized / Pending) vs. Complete (Completed / Closed / Returned). Cancelled and Expired returns are excluded."
              />
              <StatusBar
                ui={ui}
                label="In Progress"
                color={ui.warning}
                units={payload.status.in_progress.units}
                value={payload.status.in_progress.value}
                maxUnits={maxStatusUnits}
              />
              <StatusBar
                ui={ui}
                label="Complete"
                color={ui.positive}
                units={payload.status.complete.units}
                value={payload.status.complete.value}
                maxUnits={maxStatusUnits}
              />
            </Box>

            {/* ---------- Channel section ---------- */}
            <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 1, mb: 2 }}>
              <SectionHead
                title="Returns by Channel"
                subtitle={`${channelFilterLabel} · Return rate = returned units requested in this period ÷ units sold in this period (not a sale-cohort rate)`}
              />
              {(payload.by_channel || []).length === 0 ? (
                <Typography sx={{ fontSize: 13, color: colors.textTertiary, pb: 2 }}>
                  No returns in this period.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx(ui)}>Channel</TableCell>
                      <TableCell sx={headCellSx(ui, { width: "40%" })} />
                      <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                        Return Units
                      </TableCell>
                      <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                        Return Value
                      </TableCell>
                      <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                        Units Sold
                      </TableCell>
                      <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                        Return Rate
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payload.by_channel.map((row) => (
                      <TableRow key={row.channel}>
                        <TableCell sx={textCellSx(ui)}>{row.channel}</TableCell>
                        <TableCell sx={textCellSx(ui)}>
                          <Box
                            sx={{
                              height: 6,
                              borderRadius: "3px",
                              backgroundColor: ui.surfaceAlt,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${barWidthPercent(row.return_units, maxChannelUnits)}%`,
                                height: "100%",
                                backgroundColor: ui.accent,
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={numCellSx(ui)}>{formatCount(row.return_units)}</TableCell>
                        <TableCell sx={numCellSx(ui)}>{formatCurrency(row.return_value)}</TableCell>
                        <TableCell sx={numCellSx(ui)}>
                          {row.units_sold == null ? "—" : formatCount(row.units_sold)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui)}>
                          {row.return_rate == null ? "—" : `${(row.return_rate * 100).toFixed(1)}%`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            {/* ---------- Returns over time ---------- */}
            <Box sx={{ ...ui.card, p: "14px 10px 6px", height: 300, mb: 2 }}>
              <SectionHead
                title="Returns Over Time"
                subtitle="By requested date"
                right={
                  <Box sx={{ display: "flex", gap: 1.75 }}>
                    <LegendItem color={ui.accent} label="Return Value ($)" />
                    <LegendItem color="#7C3AED" label="Return Units" />
                  </Box>
                }
              />
              <ResponsiveContainer width="100%" height="80%">
                <ComposedChart data={chartData}>
                  <CartesianGrid stroke={colors.gridLine} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={{ stroke: colors.border }}
                    tick={{ fill: colors.textTertiary, fontSize: 10.5, fontFamily: MONO_FONT }}
                  />
                  <YAxis
                    yAxisId="value"
                    tickFormatter={formatCompactCurrency}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: colors.textTertiary, fontSize: 10.5, fontFamily: MONO_FONT }}
                    width={44}
                  />
                  <YAxis
                    yAxisId="units"
                    orientation="right"
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: colors.textTertiary, fontSize: 10.5, fontFamily: MONO_FONT }}
                    width={36}
                  />
                  <RechartsTooltip contentStyle={tooltipStyle} />
                  <Area
                    yAxisId="value"
                    type="monotone"
                    dataKey="return_value"
                    name="Return Value"
                    stroke={ui.accent}
                    fill={ui.accent}
                    fillOpacity={0.14}
                    strokeWidth={2}
                  />
                  <Bar yAxisId="units" dataKey="return_units" name="Return Units" fill="#7C3AED" radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            {/* ---------- Open Return Age ---------- */}
            <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 2, mb: 2 }}>
              <SectionHead
                title="Open Return Age"
                subtitle="Calendar days since requested_at, for currently open (in-progress) returns only. Select a bucket to investigate."
                right={
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ui.colors.textPrimary }}>
                    {payload.age.pct_under_30 == null ? "—" : `${payload.age.pct_under_30}%`} under 30 days
                  </Typography>
                }
              />
              {payload.age.buckets.every((b) => b.count === 0) ? (
                <Typography sx={{ fontSize: 13, color: colors.textTertiary }}>
                  No open returns in this period.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 1.5,
                  }}
                >
                  {payload.age.buckets.map((bucket) => {
                    const selected = ageBucket === bucket.bucket;
                    return (
                      <Box
                        key={bucket.bucket}
                        component="button"
                        type="button"
                        onClick={() => openAgeBucket(bucket.bucket)}
                        aria-pressed={selected}
                        aria-label={`Investigate ${bucket.bucket} day open returns`}
                        sx={{
                          fontFamily: PAGE_FONT,
                          textAlign: "left",
                          border: `1px solid ${selected ? ui.accent : "transparent"}`,
                          backgroundColor: selected ? ui.accentSoft : "transparent",
                          borderRadius: "10px",
                          p: 1,
                          m: -1,
                          cursor: "pointer",
                          color: "inherit",
                          "&:hover": {
                            backgroundColor: selected ? ui.accentSoft : ui.surfaceAlt,
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 0.5 }}>
                          {bucket.bucket} days
                        </Typography>
                        <Box
                          sx={{
                            height: 8,
                            borderRadius: "4px",
                            backgroundColor: ui.surfaceAlt,
                            overflow: "hidden",
                            mb: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: `${barWidthPercent(bucket.units, maxAgeUnits)}%`,
                              height: "100%",
                              backgroundColor: ui.accent,
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                          {formatCount(bucket.units)} units
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: colors.textTertiary }}>
                          {formatCount(bucket.count)} returns
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* ---------- SKU Return Rate ---------- */}
            <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 1 }}>
              <SectionHead
                title="SKU Return Rate"
                subtitle="Returned units requested in this period ÷ units sold in this period (not a sale-cohort rate)"
                right={
                  <TextField
                    placeholder="Search SKU"
                    size="small"
                    value={skuSearchInput}
                    onChange={(e) => setSkuSearchInput(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon sx={{ fontSize: 16, color: colors.textTertiary, mr: 0.5 }} />
                      ),
                    }}
                    sx={{ ...fieldControlSx, width: 220 }}
                  />
                }
              />
              {filteredSkuRows.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: colors.textTertiary, pb: 2 }}>
                  No SKU activity in this period.
                </Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx(ui, { cursor: "pointer" })} onClick={() => toggleSort("sku")}>
                          SKU
                        </TableCell>
                        <TableCell
                          sx={headCellSx(ui, { textAlign: "right", cursor: "pointer" })}
                          onClick={() => toggleSort("units_sold")}
                        >
                          Units Sold
                        </TableCell>
                        <TableCell
                          sx={headCellSx(ui, { textAlign: "right", cursor: "pointer" })}
                          onClick={() => toggleSort("returned_units")}
                        >
                          Returned Units
                        </TableCell>
                        <TableCell
                          sx={headCellSx(ui, { textAlign: "right", cursor: "pointer" })}
                          onClick={() => toggleSort("return_rate")}
                        >
                          Return Rate
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedSkuRows.map((row) => (
                        <TableRow
                          key={row.sku}
                          hover
                          selected={skuReasonSku === row.sku}
                          sx={{ cursor: "pointer" }}
                          onClick={() => openSkuReason(row.sku)}
                        >
                          <TableCell sx={textCellSx(ui)}>{row.sku}</TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatCount(row.units_sold)}</TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatCount(row.returned_units)}</TableCell>
                          <TableCell sx={numCellSx(ui)}>
                            {row.return_rate == null ? "—" : `${(row.return_rate * 100).toFixed(1)}%`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={filteredSkuRows.length}
                    page={skuPage}
                    onPageChange={(_, page) => setSkuPage(page)}
                    rowsPerPage={skuRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setSkuRowsPerPage(parseInt(e.target.value, 10));
                      setSkuPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{ fontSize: 12.5 }}
                  />
                </>
              )}
            </Box>
          </>
        )}
      </Box>
      <OpenReturnAgeDrawer
        open={Boolean(ageBucket)}
        onClose={() => setAgeBucket(null)}
        ui={ui}
        ageBucket={ageBucket}
        payload={agePayload}
        loading={ageLoading}
        error={ageError}
        stage={ageStage}
        onStageChange={handleAgeStageChange}
        sort={ageSort}
        onSort={toggleAgeSort}
        page={agePage}
        rowsPerPage={ageRowsPerPage}
        onPageChange={setAgePage}
        onRowsPerPageChange={(next) => {
          setAgeRowsPerPage(next);
          setAgePage(0);
        }}
        onRowClick={handleReturnRowClick}
      />
      <SkuReturnInvestigationDrawer
        open={Boolean(skuReasonSku)}
        onClose={() => {
          setSkuReasonSku(null);
          setSkuEvidenceReason(null);
        }}
        ui={ui}
        sku={skuReasonSku}
        payload={skuReasonPayload}
        loading={skuReasonLoading}
        error={skuReasonError}
        selectedReason={skuEvidenceReason}
        onSelectReason={openSkuEvidence}
        onBackToReasons={backToSkuReasons}
        evidence={skuEvidencePayload}
        evidenceLoading={skuEvidenceLoading}
        evidenceError={skuEvidenceError}
        onRetryEvidence={retrySkuEvidence}
        evidenceSort={skuEvidenceSort}
        onEvidenceSort={toggleEvidenceSort}
        evidencePage={skuEvidencePage}
        evidenceRowsPerPage={skuEvidenceRowsPerPage}
        onEvidencePageChange={setSkuEvidencePage}
        onEvidenceRowsPerPageChange={(next) => {
          setSkuEvidenceRowsPerPage(next);
          setSkuEvidencePage(0);
        }}
        onReturnClick={handleReturnRowClick}
      />
      <ReturnViewModal
        data={returnDetail}
        open={Boolean(returnDetail)}
        onClose={() => setReturnDetail(null)}
      />
    </Box>
  );
};

export default ReturnsAnalysisPage;
